import {
  BIG_ONE,
  BIG_TEN,
  BIG_TWO,
  BIG_ZERO,
} from "@/lib/tool-kit/math/bigint";

/**
 * Exact k-th roots on BigInt.
 *
 * `iroot` is ⌊n^(1/k)⌋ by Newton's method from a starting point that is never below the
 * root, so the iteration only ever decreases until it settles. Everything else is built
 * on it: a perfect-power test, and decimal digits of a root that are correct to the last
 * place — rounded by an exact integer comparison, never by a floating-point guess.
 */

/** ⌊n^(1/k)⌋ for n ≥ 0 and k ≥ 1. */
export function iroot(n: bigint, k: number): bigint {
  if (n < BIG_ZERO) {
    throw new RangeError("iroot of a negative number");
  }
  if (n < BIG_TWO || k === 1) {
    return n;
  }
  const exponent = BigInt(k);
  const less = exponent - BIG_ONE;
  const bits = n.toString(2).length;
  let x = BIG_TWO ** BigInt(Math.ceil(bits / k));
  for (;;) {
    const next = (less * x + n / x ** less) / exponent;
    if (next >= x) {
      return x;
    }
    x = next;
  }
}

/** The exact k-th root of n when n is a perfect k-th power, else null. */
export function exactRoot(n: bigint, k: number): bigint | null {
  const root = iroot(n, k);
  return root ** BigInt(k) === n ? root : null;
}

export interface RootDigits {
  /** Digits with a point, e.g. "1.41421". Rounded half up at `places` unless exact. */
  text: string;
  /** True when the root is a terminating decimal with at most `places` places. */
  exact: boolean;
  /** The integer r with r / 10^places the truncated root. */
  truncated: bigint;
}

function withPoint(scaled: bigint, places: number): string {
  const padded = scaled.toString().padStart(places + 1, "0");
  const cut = padded.length - places;
  return places === 0 ? padded : `${padded.slice(0, cut)}.${padded.slice(cut)}`;
}

/**
 * (num/den)^(1/k) to `places` decimal places, for num ≥ 0 and den > 0.
 *
 * The truncated value r/10^places comes from an integer root of ⌊num·10^(k·places)/den⌋.
 * To round, the true value is compared with the midpoint (r + ½)/10^places by raising both
 * sides to the k-th power: (2r + 1)^k · den ≤ num · 10^(k·places) · 2^k. A non-perfect
 * root is irrational, so the two are never equal and the comparison is decisive.
 */
export function rootToPlaces(
  num: bigint,
  den: bigint,
  k: number,
  places: number
): RootDigits {
  const exponent = BigInt(k);
  const scaleK = BIG_TEN ** (exponent * BigInt(places));
  const scaledNum = num * scaleK;
  const r = iroot(scaledNum / den, k);
  if (r ** exponent * den === scaledNum) {
    return { text: withPoint(r, places), exact: true, truncated: r };
  }
  const twoR1 = BIG_TWO * r + BIG_ONE;
  const roundUp = twoR1 ** exponent * den <= scaledNum * BIG_TWO ** exponent;
  return {
    text: withPoint(roundUp ? r + BIG_ONE : r, places),
    exact: false,
    truncated: r,
  };
}
